import { z } from "zod";
import { FilterSchema } from "./filters";
import { HexColorSchema, IdSchema, listOf, NameSchema } from "./primitives";
import { SceneSchema } from "./timeline";

const ChartTitleSchema = NameSchema;
const ChartLabelSchema = NameSchema;
const AxisLabelSchema = z.string().trim().min(1).max(200).optional();
const ChartColorSchema = HexColorSchema.optional();
const LabeledValueSchema = z.strictObject({
  label: ChartLabelSchema,
  value: z.number(),
  color: ChartColorSchema,
});

export const AdvisorTextBlockSchema = z.strictObject({
  type: z.literal("text"),
  markdown: z.string().max(100_000),
});
export type AdvisorTextBlock = z.infer<typeof AdvisorTextBlockSchema>;

export const AdvisorBlockSchema = z.discriminatedUnion("type", [
  AdvisorTextBlockSchema,
  z.strictObject({
    type: z.literal("bar"),
    title: ChartTitleSchema,
    xAxisLabel: AxisLabelSchema,
    yAxisLabel: AxisLabelSchema,
    data: listOf(LabeledValueSchema).min(1),
  }),
  z.strictObject({
    type: z.literal("line"),
    title: ChartTitleSchema,
    xAxisLabel: AxisLabelSchema,
    yAxisLabel: AxisLabelSchema,
    series: listOf(
      z.strictObject({
        name: ChartLabelSchema,
        color: ChartColorSchema,
        data: listOf(z.strictObject({ x: z.string(), y: z.number() })).min(1),
      }),
    ).min(1),
  }),
  z.strictObject({
    type: z.literal("scatter"),
    title: ChartTitleSchema,
    xAxisLabel: AxisLabelSchema,
    yAxisLabel: AxisLabelSchema,
    series: listOf(
      z.strictObject({
        name: ChartLabelSchema,
        color: ChartColorSchema,
        data: listOf(
          z.strictObject({
            x: z.number(),
            y: z.number(),
            label: ChartLabelSchema.optional(),
          }),
        ).min(1),
      }),
    ).min(1),
  }),
  z.strictObject({
    type: z.literal("radar"),
    title: ChartTitleSchema,
    axes: listOf(ChartLabelSchema).min(3),
    series: listOf(
      z.strictObject({
        name: ChartLabelSchema,
        color: ChartColorSchema,
        values: listOf(z.number()).min(3),
      }),
    ).min(1),
  }),
  z.strictObject({
    type: z.literal("donut"),
    title: ChartTitleSchema,
    data: listOf(LabeledValueSchema).min(1),
  }),
  z.strictObject({
    type: z.literal("parliament"),
    title: ChartTitleSchema,
    data: listOf(LabeledValueSchema.extend({ color: HexColorSchema })).min(1),
  }),
  z.strictObject({
    type: z.literal("timeline"),
    title: ChartTitleSchema,
    events: listOf(
      z.strictObject({
        id: IdSchema,
        date: z.string().trim().min(1).max(100),
        title: ChartTitleSchema,
        description: z.string().max(10_000).optional(),
      }),
    ).min(1),
  }),
  z.strictObject({
    type: z.literal("map"),
    title: ChartTitleSchema,
    /** Things on the player's own map to colour: those each filter holds for, in its colour. */
    colors: listOf(z.strictObject({ filter: FilterSchema, color: HexColorSchema })).min(1),
    legend: listOf(z.strictObject({ color: HexColorSchema, label: ChartLabelSchema })).min(1),
  }),
]);
export type AdvisorBlock = z.infer<typeof AdvisorBlockSchema>;

export const AdvisorEntrySchema = z.discriminatedUnion("from", [
  z.strictObject({
    id: IdSchema,
    from: z.literal("user"),
    blocks: listOf(AdvisorTextBlockSchema).min(1),
  }),
  z.strictObject({
    id: IdSchema,
    from: z.literal("advisor"),
    blocks: listOf(AdvisorBlockSchema).min(1),
    /** The event it is sent with, which it arrives as it plays. */
    eventId: IdSchema.optional(),
    /** A scene the answer shows, played from it. */
    scene: SceneSchema.optional(),
  }),
]);
export type AdvisorEntry = z.infer<typeof AdvisorEntrySchema>;

export const AdvisorOperationSchema = z.discriminatedUnion("type", [
  z.strictObject({ type: z.literal("advisor.entryAdded"), entry: AdvisorEntrySchema }),
  z.strictObject({ type: z.literal("advisor.cleared") }),
]);
export type AdvisorOperation = z.infer<typeof AdvisorOperationSchema>;
